/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Popover } from "./..";
import PropTypes from 'prop-types';
import { forwardRef, Fragment, memo, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { breakAll, breakWord, cursorPointer, displayBlock, displayInlineBlock, italic, overflowHidden, parseHeight, parseMaxWidth, parseMinWidth, pd, positionRelative, textCapitalize, textCenter, textColor, textLineThrough, textLowercase, textSentence, textUnderline, textUppercase, whiteSpaceBreakSpaces } from "../../styles/general";
import { useTypography as typography } from "../../theme";
import { classNames, renderHTML } from "../../utils";
const colorMap = new Map([['default', 'text/main']]);
const formatMap = new Map([['capitalize', textCapitalize], ['uppercase', textUppercase], ['lowercase', textLowercase], ['italic', italic], ['line-through', textLineThrough], ['underline', textUnderline], ['sentence', textSentence]]);
const isTextClamped = elm => (elm === null || elm === void 0 ? void 0 : elm.scrollHeight) > (elm === null || elm === void 0 ? void 0 : elm.clientHeight);
const Typography = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  capitalize,
  // deprecated
  center,
  children,
  className,
  color,
  format,
  fullWidth,
  hoverTooltip,
  italic,
  lineClamp,
  mapping,
  onClick,
  tooltipDirection,
  type,
  uppercase,
  // deprecated
  ...props
}, reference) => {
  var _ref$current;
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const formatCSS = [];
  if (Array.isArray(format)) {
    format.forEach(item => {
      formatCSS.push(formatMap.get(item));
    });
  }
  const [openTooltip, setOpenTooltip] = useState(false);
  const _TextRootCSS = [TextRootCSS(color, type, lineClamp, onClick, hoverTooltip, center, fullWidth, uppercase, capitalize), ...formatCSS];
  const showTooltip = () => {
    if (!hoverTooltip) return;
    if (isTextClamped(ref.current)) {
      setOpenTooltip(true);
    }
  };
  const hideTooltip = () => {
    if (!hoverTooltip) return;
    if (isTextClamped(ref.current)) {
      setOpenTooltip(false);
    }
  };
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const classes = classNames('DGN-UI-Typography', `DGN-UI-Typography-${type}`, className);
  const content = Array.isArray(children) && !children.some(node => node && String(node.$$typeof) === 'Symbol(react.element)') ? children.join('') : children;
  const renderTooltip = hoverTooltip ? jsx(Popover, {
    css: parseMinWidth(300),
    open: openTooltip,
    pressESCToClose: false,
    fullScreen: false,
    bgColor: 'fill/tooltip',
    anchor: ref,
    width: ((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientWidth) || 'max-content',
    direction: tooltipDirection
  }, renderHTML(content, mapping, {
    css: [_TextRootCSS, PopoverContentCSS],
    className: classes
  })) : null;
  return useMemo(() => {
    return jsx(Fragment, null, renderHTML(content, mapping, {
      ref: ref,
      css: [_TextRootCSS, lineClamp && ClampCSS(lineClamp)],
      className: classes,
      onMouseEnter: showTooltip,
      onMouseLeave: hideTooltip,
      onClick: onClick,
      ...props
    }), renderTooltip);
  }, [capitalize, center, children, className, color, format, fullWidth, hoverTooltip, italic, lineClamp, mapping, onClick, props, tooltipDirection, type, uppercase, openTooltip]);
}));
const TextRootCSS = (color, type, lineClamp, onClick, hoverTooltip, center, fullWidth, uppercase, capitalize) => css`
    ${displayInlineBlock};
    ${positionRelative};
    ${onClick && cursorPointer};
    ${hoverTooltip && lineClamp === 1 ? breakAll : breakWord};
    ${whiteSpaceBreakSpaces};
    ${textColor(colorMap.get(color) || color)};
    ${parseMaxWidth('max-content')};
    ${type !== 'inherit' && typography[type]};
    ${center && textCenter};
    ${uppercase && textUppercase};
    ${capitalize && textCapitalize};
    ${fullWidth && css`
        ${displayBlock};
        ${parseMaxWidth('initial')};
    `}
`;
const ClampCSS = lineClamp => css`
    ${overflowHidden};
    display: -webkit-box;
    -webkit-line-clamp: ${lineClamp};
    -webkit-box-orient: vertical;
`;
const PopoverContentCSS = css`
    ${textColor('text/tooltip')};
    ${pd([1, 1.5])};
    ${parseHeight('fit-content')};
`;
Typography.defaultProps = {
  center: false,
  className: '',
  color: 'default',
  format: [],
  fullWidth: true,
  hoverTooltip: false,
  mapping: 'span',
  tooltipDirection: 'bottom',
  type: 'p1'
};

/** type of text<br/>
 * h1: Title <br/>
 * h2: Header Popup <br/>
 * h3: Header popup <br/>
 * h4: Button, Tab, Title group <br/>
 * h5: Label column, Title group <br/>
 * h6: Label form <br/>
 * p1: Body text <br/>
 * p2: Body text <br/>
 * p3: Notice, Tooltip <br/>
 */
export const arrTypeTypography = ['t1', 't2', 't3', 't4', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p1', 'p2', 'p3', 'title1', 'title2', 'title3', 'title4', 'heading1', 'heading2', 'heading3', 'heading4', 'heading5', 'heading6', 'paragraph1', 'paragraph2', 'paragraph3'];
Typography.propTypes = {
  /** If `true`, text-align is center. */
  center: PropTypes.bool,
  /** The content of the component. */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** Color of text ['primary', 'secondary', 'success', 'warning', 'danger', 'info', '#hex', rgb(#,#,#)]. */
  color: PropTypes.string,
  /** Option to make the text bold, italic, capitalize or underline. */
  format: PropTypes.arrayOf(PropTypes.oneOf(['capitalize', 'uppercase', 'lowercase', 'italic', 'line-through', 'underline', 'sentence', 'none'])),
  /** If `true`, the component will take up the full width of its container. */
  fullWidth: PropTypes.bool,
  /** If `true`, hover will show tooltip when text clamped. */
  hoverTooltip: PropTypes.bool,
  /** The line-clamp property truncates text at a specific number of lines. */
  lineClamp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  /** The component used for the root node. Either a string to use a HTML element or a component. */
  mapping: PropTypes.string,
  /** Callback fired when the component is clicked. */
  onClick: PropTypes.func,
  /** Accepts all props which `mapping` tag support. */
  props: PropTypes.object,
  /** Tooltip direction. */
  tooltipDirection: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  /** Applies the theme typography styles. */
  type: PropTypes.oneOf([...arrTypeTypography, 'inherit'])
};
export default Typography;